import { Grid, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { Dialog, Select, TextField } from 'web-components-ts';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batchStatusList } from 'attrs/batchStatus';
import { Machine } from 'model/batch/machine';
import { fetchMachines } from 'api/machineApi';
import { ERROR, SUCCESS } from 'attrs/status';
import { useSnackbar } from 'notistack';
import { createMessage } from 'components/elements/snackbar/SnackBar';
import { MachineModels } from 'attrs/machineModels';
import PeriodDateSelector from 'components/PeriodDateSelector';
import moment from 'moment';
import { postBatchRecord, putBatchRecord } from 'api/batchApi';
import { BatchRecord } from 'model/batch/batchRecord';
import { batchTargetList } from 'attrs/batchTarget';
import { isNumber } from 'helpers/utils';
import { useBatchDetails } from 'containers/BatchRecords/BatchRecordsDetail/BatchDetailsContext';
import DurationInput from 'components/DurationInput';
import { durationToSeconds, hasInvalidTime, secondsToDuration } from 'components/DurationInput/durationInputUtils';
import { useBatchList } from './BatchListContext';
import CancelAddEditBatchDialog from './CancelAddEditBatchDialog';

const statusList = batchStatusList.map(status => ({
  value: status,
  labelKey: `enum.batch_status.${status}`
}));

const targetList = batchTargetList.map(status => ({
  value: status,
  labelKey: `enum.batch_target.${status}`
}));

const manual = 'MANUAL';

interface AddEditBatchDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  initialBatchRecord: BatchRecord | undefined;
}

export default function AddEditBatchDialog({ open, setOpen, initialBatchRecord = undefined }: AddEditBatchDialogProps) {
  const { t } = useTranslation();
  const reloadBatchDetails = useBatchDetails();
  const reloadBatchList = useBatchList();

  const [machineList, setMachineList] = useState<Machine[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<{ value: string; labelKey: string } | null>(null);
  const [batchId, setBatchId] = useState('');
  const [period, setPeriod] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [progress, setProgress] = useState('');
  const [size, setSize] = useState('');
  // optional fields for NETZSCH machines
  const [recipe, setRecipe] = useState<string | undefined>(undefined);
  const [maxTemp, setMaxTemp] = useState('');
  const [selectedTargetType, setSelectedTargetType] = useState<{ value: string; labelKey: string } | null>(null);
  const [targetValue, setTargetValue] = useState('');
  const [targetDuration, setTargetDuration] = useState('');
  const [cycles, setCycles] = useState('');
  const [grindingEnergy, setGrindingEnergy] = useState('');
  // cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const [errors, setErrors] = useState({
    selectedMachine: false,
    selectedStatus: false,
    batchId: false,
    period: false
  });

  const [extraFieldErrors, setExtraFieldErrors] = useState({
    targetValue: false
  });

  const resetExtraFieldErrors = () => {
    setExtraFieldErrors({
      targetValue: false
    });
  };

  const resetStates = () => {
    setSelectedMachine(null);
    setSelectedStatus(null);
    setBatchId('');
    setPeriod('');
    setStartDate('');
    setEndDate('');
    setProgress('');
    setSize('');
    setRecipe(undefined);
    setMaxTemp('');
    setSelectedTargetType(null);
    setTargetValue('');
    setTargetDuration('');
    setCycles('');
    setGrindingEnergy('');

    setErrors({
      selectedMachine: false,
      selectedStatus: false,
      batchId: false,
      period: false
    });

    resetExtraFieldErrors();
  };

  const isInvalidTargetValueOrDuration = () => {
    if (selectedTargetType?.value === 'TIME') return targetDuration.trim() === '' || hasInvalidTime(targetDuration);
    if (!!selectedTargetType && selectedTargetType?.value !== 'NONE') return targetValue.trim() === '';
    return false;
  };

  const validateFields = () => {
    const commonErrors = {
      selectedMachine: !selectedMachine,
      selectedStatus: !selectedStatus,
      batchId: batchId.trim() === '',
      period: period.trim() === ''
    };
    setErrors(commonErrors);
    const isNetzsch = selectedMachine?.brand === MachineModels.NETZSCH;
    const additionalErrors = {
      targetValue: isNetzsch ? isInvalidTargetValueOrDuration() : false
    };
    setExtraFieldErrors(additionalErrors);
    return !Object.values(commonErrors).some(Boolean) && !Object.values(additionalErrors).some(Boolean);
  };

  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const machines = await fetchMachines();
        setMachineList(machines);

        if (open && initialBatchRecord) {
          const machine = machines.find(m => m.id === initialBatchRecord.machine_id) || null;
          setSelectedMachine(machine);

          const status = statusList.find(s => s.value === initialBatchRecord.details?.status) || null;
          setSelectedStatus(status);

          setBatchId(initialBatchRecord.details?.batch_id || '');
          setStartDate(initialBatchRecord.details?.date?.start || '');
          setEndDate(initialBatchRecord.details?.date?.end || '');

          const rawProgress = initialBatchRecord.details?.progress;
          setProgress(isNumber(rawProgress) ? Number(rawProgress).toFixed(0) : '');

          const rawSize = initialBatchRecord.details?.size;
          setSize(isNumber(rawSize) ? Number(rawSize).toFixed(1) : '');

          const rawMaxTemp = initialBatchRecord.details?.max_allowed_temperature;
          setMaxTemp(isNumber(rawMaxTemp) ? Number(rawMaxTemp).toFixed(1) : '');

          if (machine?.brand === MachineModels.NETZSCH) {
            setRecipe(initialBatchRecord.details?.recipe || undefined);
            setCycles(initialBatchRecord.details?.cycles?.toString() || '');

            const rawGrindingEnergy = initialBatchRecord.property?.grinding_energy;
            setGrindingEnergy(isNumber(rawGrindingEnergy) ? Number(rawGrindingEnergy).toFixed(1) : '');

            const targetType = targetList.find(tt => tt.value === initialBatchRecord.target?.type) || null;
            setSelectedTargetType(targetType);

            const rawTargetValue = initialBatchRecord.target?.value;
            if (!!targetType && targetType?.value !== 'TIME' && targetType?.value !== 'NONE') {
              setTargetValue(isNumber(rawTargetValue) ? Number(rawTargetValue).toFixed(1) : '');
            }
            if (!!targetType && targetType?.value === 'TIME') {
              setTargetDuration(secondsToDuration(rawTargetValue));
            }
          }

          if (initialBatchRecord.details?.date?.start && initialBatchRecord.details?.date?.end) {
            const formattedStart = moment(initialBatchRecord.details.date.start).format('DD/MM/YYYY HH:mm');
            const formattedEnd = moment(initialBatchRecord.details.date.end).format('DD/MM/YYYY HH:mm');
            setPeriod(`${formattedStart} - ${formattedEnd}`);
          }
        }
      } catch (error) {
        enqueueSnackbar(createMessage('batch_records.add_edit_dialog.messages.error_loading_machines', ERROR, t), {
          variant: 'default',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
        });
      }
    };
    if (open) fetchData();
  }, [open]);

  useEffect(() => {
    if (open && !initialBatchRecord) {
      // Reset states when the dialog opens
      resetStates();
    }
  }, [open, initialBatchRecord]);

  const handleClose = () => {
    resetStates();
    setOpen(false);
  };

  const wasFormChanged = () => {
    const isEditing = !!initialBatchRecord;

    if (!isEditing) {
      return !!(
        selectedMachine ||
        selectedStatus ||
        batchId ||
        period ||
        progress ||
        size ||
        recipe ||
        maxTemp ||
        selectedTargetType ||
        targetValue ||
        cycles ||
        grindingEnergy
      );
    }

    const details = initialBatchRecord.details || {};
    const property = initialBatchRecord.property || {};
    const target = initialBatchRecord.target || {};
    const targetValueOrDuration =
      selectedTargetType?.value === 'TIME' ? durationToSeconds(targetDuration) : targetValue;
    return (
      selectedMachine?.id !== initialBatchRecord.machine_id ||
      selectedStatus?.value !== details.status ||
      batchId !== details.batch_id ||
      startDate !== details.date?.start ||
      endDate !== details.date?.end ||
      progress !== (details.progress?.toString() || '') ||
      Number(size) !== Number(details.size ?? '') ||
      Number(maxTemp) !== Number(details.max_allowed_temperature ?? '') ||
      recipe !== (details.recipe || '') ||
      cycles !== (details.cycles?.toString() || '') ||
      Number(grindingEnergy) !== Number(property.grinding_energy ?? '') ||
      selectedTargetType?.value !== target.type ||
      Number(targetValueOrDuration) !== Number(target.value ?? '')
    );
  };

  const onClickCancelCloseAddEditBatch = () => {
    if (wasFormChanged()) {
      setCancelDialogOpen(true);
    } else {
      handleClose();
    }
  };

  const onClickOKAddEditBatch = () => {
    if (validateFields()) {
      const saveBatch = async () => {
        try {
          const isNetzsch = selectedMachine?.brand === MachineModels.NETZSCH;
          const newCycles = cycles ? Number(cycles) : undefined;
          const hasGrindingEnergy = isNetzsch && grindingEnergy;
          const newGrindingEnergy = grindingEnergy ? Number(grindingEnergy) : undefined;
          const hasTargetValue = isNetzsch && selectedTargetType?.value;
          const newTargetValue = targetValue ? Number(targetValue) : undefined;
          const batch = new BatchRecord({
            gateway_id: selectedMachine?.gateway_id,
            machine_id: selectedMachine?.id,
            origin: manual,
            details: {
              batch_id: batchId,
              cycles: isNetzsch ? newCycles : undefined,
              date: {
                start: startDate,
                end: endDate
              },
              id: batchId,
              max_allowed_temperature: maxTemp ? Number(maxTemp) : undefined,
              progress: progress ? Number(progress) : undefined,
              recipe: isNetzsch ? recipe || undefined : undefined,
              size: size ? Number(size) : undefined,
              status: selectedStatus?.value
            },
            property: hasGrindingEnergy
              ? {
                  grinding_energy: newGrindingEnergy
                }
              : undefined,
            target: hasTargetValue
              ? {
                  type: selectedTargetType?.value,
                  value: selectedTargetType?.value === 'TIME' ? durationToSeconds(targetDuration) : newTargetValue
                }
              : undefined
          });
          const batchUUID = initialBatchRecord?.details?.id;
          if (batchUUID) {
            await putBatchRecord(batchUUID, batch);
            reloadBatchDetails();
          } else {
            await postBatchRecord(batch);
            reloadBatchList();
          }
          handleClose();
          enqueueSnackbar(createMessage('batch_records.add_edit_dialog.messages.success', SUCCESS, t), {
            variant: 'default',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
          });
        } catch (error) {
          enqueueSnackbar(createMessage('batch_records.add_edit_dialog.messages.error', ERROR, t), {
            variant: 'default',
            autoHideDuration: 3000,
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
          });
        }
      };
      saveBatch();
    }
  };

  const onChangeMachine = (event: SelectChangeEvent<unknown>) => {
    const machine = machineList.find(item => item.id === event.target.value) || null;
    const selectedIsNetzsch = selectedMachine?.brand === MachineModels.NETZSCH;
    const currentIsNetzsch = machine?.brand === MachineModels.NETZSCH;
    // validation cleanup triggered if the validation type has changed
    // it can be NETZSCH or other
    if (selectedIsNetzsch !== currentIsNetzsch) {
      resetExtraFieldErrors();
    }
    setSelectedMachine(machine);
  };

  const onChangeStatus = (event: SelectChangeEvent<unknown>) => {
    const status = statusList.find(item => item.value === event.target.value) || null;
    setSelectedStatus(status);
  };

  const onChangeTarget = (event: SelectChangeEvent<unknown>) => {
    const targetType = targetList.find(item => item.value === event.target.value) || null;
    if (event.target.value === 'NONE') setTargetValue('');
    if (event.target.value !== 'TIME') setTargetDuration('');
    setSelectedTargetType(targetType);
  };

  const onChangePeriod = (startDateParam: string, endDateParam: string) => {
    const formattedStart = moment(startDateParam).format('DD/MM/YYYY HH:mm');
    const formattedEnd = moment(endDateParam).format('DD/MM/YYYY HH:mm');
    setPeriod(`${formattedStart} - ${formattedEnd}`);
    setStartDate(startDateParam);
    setEndDate(endDateParam);
  };

  function cannotEdit() {
    return !!initialBatchRecord && initialBatchRecord.origin !== manual;
  }

  const onChangeDecimal = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setValue: Dispatch<SetStateAction<string>>
  ) => {
    const { value } = e.target;
    const [, decimal] = value.split('.');
    if (!decimal || decimal?.length <= 1) {
      setValue(value);
    }
  };

  const onChangeProgress = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = e.target;
    if (value === '') {
      setProgress(value);
      return;
    }
    const numericValue = Number(value);
    if (numericValue >= 0 && numericValue <= 999) {
      setProgress(value);
    }
  };

  const renderTargetField = () => {
    if (!selectedTargetType?.value || selectedTargetType?.value === 'NONE') {
      return <></>;
    }
    if (selectedTargetType?.value === 'TIME') {
      return (
        <DurationInput
          name="target_value"
          required
          disabled={cannotEdit()}
          label={t('batch_records.add_edit_dialog.target_value')}
          value={targetDuration}
          onChange={e => setTargetDuration(e.target.value)}
          error={extraFieldErrors.targetValue}
          helperText={extraFieldErrors.targetValue ? t('batch_records.add_edit_dialog.validation.required') : ''}
          fullWidth
        />
      );
    }
    return (
      <TextField
        id="target_value"
        type="number"
        required
        disabled={cannotEdit()}
        label={t('batch_records.add_edit_dialog.target_value')}
        value={targetValue}
        onChange={e => onChangeDecimal(e, setTargetValue)}
        error={extraFieldErrors.targetValue}
        helperText={extraFieldErrors.targetValue ? t('batch_records.add_edit_dialog.validation.required') : ''}
        fullWidth
      />
    );
  };

  const dialogContent = (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>
            {t('batch_records.add_edit_dialog.batch_status')}
          </Typography>
        </Grid>
        <Grid item sm={6} xs={12}>
          <Select
            id="machine-select"
            data-testid="machine-combobox"
            handleChange={onChangeMachine}
            value={selectedMachine?.id || ''}
            label={t('batch_records.add_edit_dialog.machine_name')}
            helperText={t('batch_records.add_edit_dialog.validation.required')}
            error={errors.selectedMachine}
            required
            fullWidth
            withEmptyField
            disabled={cannotEdit()}
          >
            {machineList.map(machine => (
              <MenuItem key={machine.id} value={machine.id}>
                {machine.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item sm={6} xs={12}>
          <TextField
            id="batch-id"
            label={t('batch_records.add_edit_dialog.batch_id')}
            required
            disabled={cannotEdit()}
            value={batchId}
            onChange={e => setBatchId(e.target.value)}
            error={errors.batchId}
            helperText={errors.batchId ? t('batch_records.add_edit_dialog.validation.required') : ''}
            size="medium"
            sx={{
              width: '100%'
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PeriodDateSelector
            id="period"
            label={t('batch_records.add_edit_dialog.period')}
            required
            value={period}
            maxDays={0}
            onChange={onChangePeriod}
            error={errors.period}
            helperText={errors.period ? t('batch_records.add_edit_dialog.validation.required') : ''}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <Select
            id="status-select"
            data-testid="status-select"
            handleChange={onChangeStatus}
            value={selectedStatus?.value || ''}
            label={t('batch_records.add_edit_dialog.status')}
            helperText={t('batch_records.add_edit_dialog.validation.required')}
            error={errors.selectedStatus}
            required
            fullWidth
            withEmptyField
            disabled={cannotEdit()}
          >
            {statusList.map(status => (
              <MenuItem key={status.value} value={status.value}>
                {t(status.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item sm={4} xs={12}>
          <TextField
            id="progress"
            type="number"
            inputProps={{ step: 1, min: 0, max: 999 }}
            label={t('batch_records.add_edit_dialog.progress')}
            disabled={cannotEdit()}
            value={progress}
            onChange={onChangeProgress}
            onKeyDown={e => {
              if (e.key === '.' || e.key === ',') {
                e.preventDefault();
              }
            }}
            size="medium"
            sx={{
              width: '100%'
            }}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <TextField
            id="size"
            type="number"
            label={t('batch_records.add_edit_dialog.size')}
            disabled={cannotEdit()}
            value={size}
            onChange={e => onChangeDecimal(e, setSize)}
            size="medium"
            sx={{
              width: '100%'
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ marginTop: '20px' }}>
        <Grid item xs={12}>
          <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>
            {t('batch_records.add_edit_dialog.batch_details')}
          </Typography>
        </Grid>
        {/* Dynamic fields based on the selected machine brand */}
        {selectedMachine?.brand === MachineModels.NETZSCH ? (
          <>
            <Grid item sm={6} xs={12}>
              <TextField
                id="recipe"
                label={t('batch_records.add_edit_dialog.recipe')}
                disabled={cannotEdit()}
                value={recipe}
                onChange={e => setRecipe(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                id="max_allowed_temperature"
                type="number"
                label={t('batch_records.add_edit_dialog.maximum_temperature')}
                disabled={cannotEdit()}
                value={maxTemp}
                onChange={e => onChangeDecimal(e, setMaxTemp)}
                fullWidth
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Select
                id="target-type-select"
                data-testid="target-type-select"
                handleChange={onChangeTarget}
                value={selectedTargetType?.value || ''}
                label={t('batch_records.add_edit_dialog.target_type')}
                fullWidth
                withEmptyField
                disabled={cannotEdit()}
              >
                {targetList.map(targetType => (
                  <MenuItem key={targetType.value} value={targetType.value}>
                    {t(targetType.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item sm={6} xs={12}>
              {renderTargetField()}
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                id="cycles"
                type="number"
                inputProps={{ step: 1, min: 0 }}
                disabled={cannotEdit()}
                label={t('batch_records.add_edit_dialog.cycles')}
                value={cycles}
                onChange={e => setCycles(e.target.value)}
                onKeyDown={e => {
                  if (e.key === '.' || e.key === ',') {
                    e.preventDefault();
                  }
                }}
                fullWidth
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextField
                id="grinding_energy"
                type="number"
                label={t('batch_records.add_edit_dialog.grinding_energy')}
                disabled={cannotEdit()}
                value={grindingEnergy}
                onChange={e => onChangeDecimal(e, setGrindingEnergy)}
                fullWidth
              />
            </Grid>
          </>
        ) : (
          <Grid item sm={6} xs={12}>
            <TextField
              id="max_allowed_temperature"
              type="number"
              label={t('batch_records.add_edit_dialog.maximum_temperature')}
              disabled={cannotEdit()}
              value={maxTemp}
              onChange={e => onChangeDecimal(e, setMaxTemp)}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <CancelAddEditBatchDialog open={cancelDialogOpen} setOpen={setCancelDialogOpen} handleCancel={handleClose} />
    </>
  );

  return open ? (
    <Dialog
      open={open}
      handleClose={onClickCancelCloseAddEditBatch}
      handleCancel={onClickCancelCloseAddEditBatch}
      handleOk={onClickOKAddEditBatch}
      title={t(`batch_records.add_edit_dialog.${initialBatchRecord ? 'edit_title' : 'title'}`)}
      subTitle={t(`batch_records.add_edit_dialog.${initialBatchRecord ? 'edit_subtitle' : 'subtitle'}`)}
      cancelCaption={t('default_actions.cancel')}
      okCaption={t('default_actions.save')}
      maxHeight="98vh"
      maxWidth="800px"
      canSave
      content={dialogContent}
    />
  ) : null;
}
